// app/features/templates/components/TemplateFilterComponent.tsx
"use client";

import { Search, X } from "lucide-react";
import { useQueryState } from "nuqs";
import React from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useGetActiveCategoriesQuery } from "../services_and_hooks/useTemplatesQueryHook";
import { TemplateCategory } from "../templates.interface";

interface TemplateFilterComponentProps {
  initialSearch?: string;
  initialCategory?: string;
}

export function TemplateFilterComponent({
  initialSearch = "",
  initialCategory = "all",
}: TemplateFilterComponentProps) {
  // Using individual useQueryState hooks for each param
  const [searchTerm, setSearchTerm] = useQueryState("search", {
    defaultValue: initialSearch,
    clearOnDefault: true,
  });

  const [category, setCategory] = useQueryState("category", {
    defaultValue: initialCategory,
    clearOnDefault: true,
  });

  // Fetch available categories dynamically
  const { data: activeCategories = [], isLoading: isLoadingActiveCategories } =
    useGetActiveCategoriesQuery();

  const hasActiveFilters = searchTerm || (category && category !== "all");

  const clearAllFilters = () => {
    setSearchTerm("");
    setCategory("all");
  };

  return (
    <div className="bg-beige-dark rounded-lg p-6 mb-8">
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-end">
        {/* Search Input */}
        <div className="flex-1 w-full lg:max-w-md">
          <label
            htmlFor="search"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Search Templates
          </label>
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={16}
            />
            <Input
              id="search"
              type="text"
              placeholder="Search by title, description, or tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white border-gray-300"
            />
          </div>
        </div>

        {/* Category Filter */}
        <div className="w-full lg:w-48">
          <label
            htmlFor="category"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Category
          </label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="bg-white border-gray-300 hover:bg-gray-50 transition-colors">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent
              className="bg-white rounded-xl shadow-lg py-2 z-50 border border-gray-100 w-[220px]"
              position="popper"
              sideOffset={5}
            >
              <SelectItem
                value="all"
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-display font-medium text-gray-700 hover:bg-gray-50 text-left transition-colors cursor-pointer"
              >
                All Categories
              </SelectItem>
              {isLoadingActiveCategories ? (
                <SelectItem
                  value="loading"
                  disabled
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-display font-medium text-gray-400 text-left"
                >
                  Loading categories...
                </SelectItem>
              ) : (
                activeCategories.map((cat: TemplateCategory) => (
                  <SelectItem
                    key={cat.slug}
                    value={cat.slug}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-display font-medium text-gray-700 hover:bg-gray-50 text-left transition-colors cursor-pointer"
                  >
                    {cat.icon_name && `${cat.icon_name} `}
                    {cat.name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <Button
            variant="outline"
            onClick={clearAllFilters}
            className="flex items-center gap-2 whitespace-nowrap"
          >
            <X size={16} />
            Clear Filters
          </Button>
        )}
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-sm text-gray-500">Active filters:</span>
            {searchTerm && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-sm rounded-md">
                Search: "{searchTerm}"
                <button
                  onClick={() => setSearchTerm("")}
                  className="text-blue-500 hover:text-blue-700"
                >
                  <X size={14} />
                </button>
              </span>
            )}
            {category && category !== "all" && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 text-sm rounded-md">
                Category:{" "}
                {activeCategories.find(
                  (c: TemplateCategory) => c.slug === category,
                )?.name || category}
                <button
                  onClick={() => setCategory("all")}
                  className="text-purple-500 hover:text-purple-700"
                >
                  <X size={14} />
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
